import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./EditFormComponent.css";
import FieldRenderer from "./FieldRenderer";
import PasswordModal from "./PasswordModal";
import { getSanitizedPayload } from "./utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import TableLoader from "../TableComponent/TableLoader";

const EditFormComponent = ({
  fields,
  formData,
  onChange,
  modelId,
  Put,
  file,
  model,
}) => {
  const isCreate = !modelId;
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [decryptedValue, setDecryptedValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // NEW: bootstrapping/loading for dropdown APIs + formData presence
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const navigate = useNavigate();

  // ---- Submit (memoized for Ctrl+S listener) ----
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const isCreate = !modelId;
    const url = isCreate ? `${Put}` : `${Put}/${modelId}`;
    const method = isCreate ? "POST" : "PUT";

    try {
      const payload = getSanitizedPayload(formData, fields);
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(
          isCreate ? "✅ Created successfully" : "✅ Data updated successfully",
          { position: "top-right", autoClose: 1500 }
        );

        setTimeout(() => {
          if (isCreate && data?._id) {
            navigate(`/edit/${model}/${data._id}`);
          } else {
            navigate(`/table/${model}`);
          }
        }, 1600);
      } else {
        toast.error(
          data?.message || (isCreate ? "Create failed" : "Update failed"),
          {
            position: "top-right",
          }
        );
      }
    } catch (err) {
      console.error("🔥 Submit error:", err);
      toast.error("Error submitting form", { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  }, [Put, model, modelId, formData, fields, isSubmitting, navigate]);

  const apiSignature = useMemo(
    () =>
      JSON.stringify(
        (fields || [])
          .filter((f) => !!f.api)
          .map((f) => f.api)
          .sort()
      ),
    [fields]
  );

  // ---- Ctrl/Cmd + S -> Save ----
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSubmit]);

  // ---- Dropdowns (cache by both field *and* api to support different consumers) ----
  useEffect(() => {
    let cancelled = false;

    const fetchDropdowns = async () => {
      // Gather fields with APIs
      const apiFields = (fields || []).filter((f) => !!f.api);

      // If nothing to fetch and we already have formData, we’re ready
      if (apiFields.length === 0) {
        setIsBootstrapping(false);
        return;
      }

      setIsBootstrapping(true);
      const newOptions = {};
      const tasks = apiFields.map(async (f) => {
        try {
          const res = await fetch(f.api);
          const data = await res.json();
          if (f.field) newOptions[f.field] = data;
          newOptions[f.api] = data; // also by API URL for reuse elsewhere
        } catch (err) {
          // Don’t block UI; log the error
          console.error(`Failed to load options for ${f.field || f.api}`, err);
        }
      });

      await Promise.allSettled(tasks);
      if (!cancelled) {
        setDropdownOptions((prev) => ({ ...prev, ...newOptions }));
        // Only mark bootstrapping done if formData is also present
        setIsBootstrapping(false);
      }
    };

    fetchDropdowns();
    return () => {
      cancelled = true;
    };
  }, [apiSignature]);



  // ---- Password helpers ----
  const handlePasswordChange = async () => {
    const field = fields.find((f) => f.type === "password");
    if (!field?.ChangePassword || !modelId) return;

    try {
      const res = await fetch(`${field.ChangePassword}/${modelId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Password updated", { autoClose: 1200 });
        setNewPassword("");
        setShowPasswordChange(false);
      } else {
        toast.error(data?.message || "Error changing password");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error changing password");
    }
  };

  const handleDecrypt = async () => {
    const field = fields.find((f) => f.type === "plainPassword");
    if (!field?.Decrypt || !modelId) return;

    try {
      const res = await fetch(`${field.Decrypt}/${modelId}`);
      const data = await res.json();
      if (res.ok && data?.plainPassword) setDecryptedValue(data.plainPassword);
      else toast.error("Failed to decrypt password");
    } catch (err) {
      console.error(err);
      toast.error("Error decrypting password");
    }
  };


  return (
    <div className="edit-form">
      {isBootstrapping ? (
        <TableLoader />
      ) : (
        <>
          {(fields || []).map((col, i) => (
            <FieldRenderer
              key={col.field || col.label || i}
              col={col}
              formData={formData}
              dropdownOptions={dropdownOptions}
              decryptedValue={decryptedValue}
              onChange={onChange}
              setShowPasswordChange={setShowPasswordChange}
              handleDecrypt={handleDecrypt}
              isCreate={isCreate}
              modelName={model}
            />
          ))}

          {!isCreate && showPasswordChange && (
            <PasswordModal
              newPassword={newPassword}
              setNewPassword={setNewPassword}
              onCancel={() => setShowPasswordChange(false)}
              onSave={handlePasswordChange}
            />
          )}

          <div className="form-actions">
            <button
              onClick={handleSubmit}
              className="btn-editForm-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Submit"}
            </button>
          </div>
        </>
      )}

      <ToastContainer />
    </div>
  );
};

export default EditFormComponent;
